/*
 *   File : change-password-modal.js
 *   Author : https://evoqins.com
 *   Description : change password modal component
 *   Integrations : auth/change-password
 *   Version : 1.1.0
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { PrimaryButton } from "../Buttons";
import { Password } from "../FormElements";
import SuccessModal from "./success-modal";
import { APIService } from "../../Service";


/* ---------------- Overlay Component ---------------- */
const ModalOverlay = ({ onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, []);

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
      style={{ zIndex: 1040 }}
      onClick={onClose}
    />
  );
};

/* ---------------- Change Password Modal ---------------- */
export default function ChangePasswordModal({
  open = false,
  onCancel,
  onSuccess, // optional callback after update
}) {
  if (!open) return null;

  /* ------------ Local State ------------ */
  const [currentPass, setCurrentPass]     = useState("");
  const [password, setPassword]           = useState("");
  const [confirmPass, setConfirmPass]     = useState("");

  const [currentPassError, setCurrentPassError]   = useState("");
  const [passwordError, setPasswordError]         = useState("");
  const [confirmPassError, setConfirmPassError]   = useState("");

  const [loading, setLoading]         = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  /* ------------ Validation ------------ */
  const validate = () => {
    let valid = true;
    setCurrentPassError("");
    setPasswordError("");
    setConfirmPassError("");

    if (!currentPass.trim()) {
      setCurrentPassError("Current password is required");
      valid = false;
    }

    if (!password.trim()) {
      setPasswordError("Password is required");
      valid = false;
    } else if (password.trim().length < 6) {
      setPasswordError("Password must be at least 6 characters");
      valid = false;
    }

    if (!confirmPass.trim()) {
      setConfirmPassError("Please confirm your new password");
      valid = false;
    } else if (password.trim() && password !== confirmPass) {
      setConfirmPassError("Passwords do not match");
      valid = false;
    }

    return valid;
  };

  /* ------------ API — Change Password ------------ */
  const _changePassword = () => {
    setLoading(true);

    const url  = "auth/change-password";
    const data = {
      current_password: currentPass,
      new_password:     password,
    };

    APIService(true, url, data).then((response) => {
      if (response.status_code === 200) {
        setSuccessModal(true);
        onSuccess?.();
      } else {
        toast.dismiss();
        toast.error(response.message, {
          type: "error",
          className: "e-toast",
        });
      }
      setLoading(false);
    });
  };

  /* ------------ Submit Handler ------------ */
  const handleUpdate = () => {
    if (!validate()) return;
    _changePassword();
  };

  return createPortal(
    <>
      {/* Overlay */}
      <ModalOverlay onClose={onCancel} />

      {/* Modal Box */}
      <div
        className="position-fixed overflow-hidden top-50 start-50 translate-middle rounded-28 shadow bg-white bg-opacity-75"
        style={{
          zIndex: 1055,
          width: "412px",
          backdropFilter: "blur(4px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-24 d-flex flex-column">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-600 fs-24 mb-0 text-eerie-black">
              Set new password
            </h5>

            <PrimaryButton
              icon="close"
              ClassName="bg-transparent p-0"
              onPress={onCancel}
            />
          </div>

          {/* Inputs */}
          <div className="row row-cols-1 pt-24">
            <div className="col">
              <Password
                id="current-pass"
                label="Current Password"
                placeholder="Enter current password"
                require={true}
                value={currentPass}
                error={currentPassError}
                onKeyPress={handleUpdate}
                className="w-100 px-3 py-10"
                handleChange={setCurrentPass}
              />

              <Password
                id="new-pass"
                label="New Password"
                placeholder="Enter new password"
                require={true}
                value={password}
                error={passwordError}
                onKeyPress={handleUpdate}
                className="w-100 px-3 py-10"
                handleChange={setPassword}
              />

              <Password
                id="confirm-pass"
                label="Confirm New Password"
                placeholder="Confirm new password"
                require={true}
                value={confirmPass}
                error={confirmPassError}
                onKeyPress={handleUpdate}
                className="w-100 px-3 py-10"
                handleChange={setConfirmPass}
              />
            </div>
          </div>

          {/* Footer Button */}
          <div className="row row-cols-1 pt-10">
            <div className="col">
              <PrimaryButton
                name={loading ? "Updating..." : "Update"}
                disabled={loading}
                loading={loading}
                ClassName="rounded-pill bg-oxford-blue text-white py-3 w-100 fs-16 fw-500"
                onPress={handleUpdate}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        title="Successful"
        message="Your password has been changed successfully."
        open={successModal}
        onConfirm={() => {
          setSuccessModal(false);
          onCancel();
        }}
      />
    </>,
    document.body,
  );
}